/**
 * DS3-0414: does the transaction already carry a REAL signer signature? A Solana
 * signature is computed over the compiled message, which INCLUDES recentBlockhash, so
 * the blockhash must not be rewritten once any signer (sponsor / multisig / co-signer)
 * has signed - a rewrite silently invalidates their signature. Legacy signatures are
 * `{ signature: Buffer | null }` entries; versioned signatures are 64-byte arrays that
 * stay all-zero placeholders until a signer fills them. Mirrors turnkey-signer-bridge.ts.
 */
export function transactionHasSignature(transaction) {
    if ('serializeMessage' in transaction) {
        return transaction.signatures.some((s) => s.signature != null);
    }
    return transaction.signatures.some((sig) => sig.some((b) => b !== 0));
}
/**
 * Extracts a human-readable error message from Solana transaction logs.
 */
export function extractTransactionError(err, logMessages) {
    if (logMessages) {
        return JSON.stringify(logMessages);
    }
    if (err) {
        try {
            return JSON.stringify(err);
        }
        catch (_a) {
            return String(err);
        }
    }
    return 'Unknown transaction error';
}
/**
 * Polls getSignatureStatuses until the transaction is confirmed, then checks for errors.
 * Throws if the transaction failed on-chain. Returns parsed transaction info on success.
 */
export async function confirmAndCheckTransaction(connection, signature) {
    var _a;
    const maxAttempts = 15;
    for (let i = 0; i < maxAttempts; i++) {
        const { value } = await connection.getSignatureStatuses([signature]);
        const status = value[0];
        if (status) {
            if (status.err) {
                const txInfo = await connection.getParsedTransaction(signature, {
                    maxSupportedTransactionVersion: 0,
                    commitment: 'confirmed'
                });
                const errorMessage = extractTransactionError(status.err, (_a = txInfo === null || txInfo === void 0 ? void 0 : txInfo.meta) === null || _a === void 0 ? void 0 : _a.logMessages);
                throw new Error(`Transaction failed: ${errorMessage}`);
            }
            if (status.confirmationStatus === 'confirmed' || status.confirmationStatus === 'finalized') {
                return await connection.getParsedTransaction(signature, {
                    maxSupportedTransactionVersion: 0,
                    commitment: 'confirmed'
                });
            }
        }
        await new Promise(resolve => setTimeout(resolve, 1000));
    }
    throw new Error('Transaction confirmation timeout');
}
